import { useContent } from '@eneco/cms';
import { familyEntryByRole } from '@eneco/cms-sanity';
import { string } from 'yup';
import type { Schema } from 'yup';

import type { FormFieldDescriptor } from './types';

/**
 * Read one swept field descriptor off the current block.
 *
 * ADR-0068 collapsed each field's object into a shared family array keyed by `role`, so
 * `GenericFormInputField.genericInputField` is now an entry of its `genericFields` array. The array
 * name and role both come from the schema (`component-map.json`), which is why they are passed in
 * rather than inferred: entries carry no `_type` to discriminate on.
 */
export const useFormField = (family: string, role: string): FormFieldDescriptor => {
  const content = useContent<Record<string, unknown>>();
  return (familyEntryByRole(content[family], role) ?? {}) as FormFieldDescriptor;
};

/** Same lookup outside React, for a component's static `formSchema`. */
export const readFormField = (content: Record<string, unknown>, family: string, role: string): FormFieldDescriptor =>
  (familyEntryByRole(content[family], role) ?? {}) as FormFieldDescriptor;

/**
 * A field's yup rule: required with the author's own message when one is set, optional otherwise.
 * Keyed by the field's `name`, which is what react-hook-form registers it under.
 */
export const fieldRule = (field: FormFieldDescriptor, fallbackName: string): Record<string, Schema<unknown>> => {
  const name = field.name || fallbackName;
  return { [name]: (field.requiredMessage ? string().required(field.requiredMessage) : string()) as Schema<unknown> };
};
